import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal } from '../hooks/useAnimations';

export default function HairTransplantSection() {
  const { language } = useLanguage();
  const [leftRef, leftVisible] = useScrollReveal(0.15);
  const [rightRef, rightVisible] = useScrollReveal(0.15);

  const features = language === 'hi' ? [
    'FUE तकनीक से प्राकृतिक दिखने वाले परिणाम',
    'न्यूनतम दर्द और तेज़ रिकवरी',
    'IADVL प्रमाणित हेयर ट्रांसप्लांट विशेषज्ञ',
    'व्यक्तिगत उपचार योजना',
    '10,000+ सफल प्रक्रियाएं',
  ] : [
    'Natural-looking results with advanced FUE technique',
    'Minimal pain and fast recovery time',
    'IADVL certified hair transplant specialist',
    'Personalized treatment plan for every patient',
    '10,000+ successful grafts performed',
  ];

  return (
    <section className="py-20 md:py-28 bg-[#0F172A] relative overflow-hidden" data-testid="hair-transplant-section">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D1A760]/[0.03] rounded-full translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#D1A760]/[0.03] rounded-full -translate-x-1/2 translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT: Content */}
          <div
            ref={leftRef}
            className={`space-y-6 transition-all duration-1000 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D1A760]/10 border border-[#D1A760]/20">
              <div className="w-2 h-2 rounded-full bg-[#D1A760]" />
              <span className="text-xs font-manrope text-[#D1A760] font-medium">
                {language === 'hi' ? 'सबसे लोकप्रिय उपचार' : 'Most Popular Treatment'}
              </span>
            </div>

            <h2 className="font-outfit font-light text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">
              {language === 'hi' ? 'हेयर ट्रांसप्लांट' : 'Hair Transplant'}
              <span className="block text-[#D1A760]">
                {language === 'hi' ? 'जो दिखे बिल्कुल असली' : 'That Looks Completely Natural'}
              </span>
            </h2>

            <p className="text-base text-white/50 font-manrope leading-relaxed max-w-md">
              {language === 'hi'
                ? 'डॉ. कुलदीप वर्मा, झाँसी के सर्वश्रेष्ठ हेयर ट्रांसप्लांट विशेषज्ञ — उन्नत FUE तकनीक से प्राकृतिक हेयरलाइन, स्थायी परिणाम।'
                : 'Best hair transplant clinic in Jhansi — Dr. Kuldeep Verma. Advanced FUE hair transplant with natural hairline design, permanent results, and minimal scarring. Expert hair fall treatment in Jhansi.'}
            </p>

            <ul className="space-y-3">
              {features.map((feature, idx) => (
                <li
                  key={idx}
                  className={`flex items-start gap-3 transition-all duration-500 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
                  style={{ transitionDelay: `${400 + idx * 100}ms` }}
                >
                  <div className="w-5 h-5 rounded-full bg-[#D1A760]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#D1A760]" />
                  </div>
                  <span className="text-sm text-white/70 font-manrope">{feature}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-4 pt-4">
              <button
                onClick={() => document.querySelector('#appointment')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-3.5 rounded-full bg-gradient-to-r from-[#D1A760] to-[#B89255] text-white font-manrope font-semibold text-sm hover:shadow-[0_8px_30px_rgba(209,167,96,0.35)] transition-all duration-300 flex items-center gap-2"
                data-testid="hair-transplant-cta"
              >
                {language === 'hi' ? 'अपॉइंटमेंट बुक करें' : 'Book Appointment'} <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="tel:+918303365990"
                className="px-6 py-3.5 rounded-full border border-white/20 text-white/70 font-manrope font-medium text-sm hover:bg-white/5 hover:text-white transition-all duration-200"
              >
                {language === 'hi' ? 'कॉल करें' : 'Call Now'}
              </a>
            </div>
          </div>

          {/* RIGHT: Before/After Split Image */}
          <div
            ref={rightRef}
            className={`relative transition-all duration-1000 delay-200 ${rightVisible ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 translate-x-12 scale-95'}`}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
              {/* Split container */}
              <div className="grid grid-cols-2 h-[420px] md:h-[480px]">
                {/* Before */}
                <div className="relative overflow-hidden">
                  <img
                    src="/hair-before.jpg"
                    alt="Before hair transplant in Jhansi - hair loss patient"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20">
                    <span className="text-white text-xs font-manrope font-semibold">Before</span>
                  </div>
                </div>

                {/* Divider */}
                <div className="absolute left-1/2 top-0 bottom-0 w-[3px] bg-[#D1A760] z-10 -translate-x-1/2">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#D1A760] flex items-center justify-center shadow-lg">
                    <ArrowRight className="w-4 h-4 text-white" />
                  </div>
                </div>

                {/* After */}
                <div className="relative overflow-hidden">
                  <img
                    src="/hair-after.jpg"
                    alt="After hair transplant in Jhansi - natural results by Dr. Kuldeep Verma"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-[#D1A760]/80 backdrop-blur-md">
                    <span className="text-white text-xs font-manrope font-semibold">After</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating stats badge */}
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] px-6 py-3 flex items-center gap-6">
              <div className="text-center">
                <p className="font-outfit font-semibold text-lg text-[#0F172A]">4000+</p>
                <p className="text-[10px] text-[#94A3B8] font-manrope">Grafts</p>
              </div>
              <div className="w-px h-8 bg-slate-200" />
              <div className="text-center">
                <p className="font-outfit font-semibold text-lg text-[#D1A760]">98%</p>
                <p className="text-[10px] text-[#94A3B8] font-manrope">Success</p>
              </div>
              <div className="w-px h-8 bg-slate-200" />
              <div className="text-center">
                <p className="font-outfit font-semibold text-lg text-[#0F172A]">Natural</p>
                <p className="text-[10px] text-[#94A3B8] font-manrope">Results</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
